import type { AppLang } from '../detectLang';

export type DashboardMessages = {
  welcome: string;
  features: {
    // USER
    mPhotoPro: { title: string; description: string };
    mPhotoLite: { title: string; description: string };
    mPhotoMono: { title: string; description: string };
    livePhotos: { title: string; description: string };
    videos: { title: string; description: string };
    sePayPayment: { title: string; description: string };
    eventManagement: { title: string; description: string };
    profile: { title: string; description: string };
    themes: { title: string; description: string };
    customerInformation: { title: string; description: string };
    software: { title: string; description: string };
    aiCreate: { title: string; description: string };
    deviceManagement: { title: string; description: string };
    userStaff: { title: string; description: string };
    // ADMIN / STAFF
    userManagement: { title: string; description: string };
    staffManagement: { title: string; description: string };
    customFrame: { title: string; description: string };
    album: { title: string; description: string };
    landingPage: { title: string; description: string };
    desktopManagement: { title: string; description: string };
    updateSoftware: { title: string; description: string };
    emailManagement: { title: string; description: string };
    backgroundAi: { title: string; description: string };
  };
};

export const dashboardMessages: Record<AppLang, DashboardMessages> = {
  en: {
    welcome: 'Welcome, {username}!',
    features: {
      mPhotoPro: {
        title: 'M-Photo Pro',
        description: 'Upload and manage your photos',
      },
      mPhotoLite: {
        title: 'M-Photo Lite',
        description: 'Gallery server, PNG icons, and sync from the Android app',
      },
      mPhotoMono: {
        title: 'M-Photo Mono',
        description: 'Add secondary photos and mono frames for your account',
      },
      livePhotos: {
        title: 'Live Photos',
        description: 'View and manage your live photos',
      },
      videos: {
        title: 'Videos',
        description: 'Upload and manage your videos',
      },
      sePayPayment: {
        title: 'SePay Payment',
        description: 'Configure SePay gateway and create VietQR codes',
      },
      eventManagement: {
        title: 'Event Management',
        description: 'Manage events and time frames',
      },
      profile: {
        title: 'Profile',
        description: 'View and edit your profile',
      },
      themes: {
        title: 'Themes',
        description: 'Manage themes and organize frames',
      },
      customerInformation: {
        title: 'Customer Information',
        description: 'Manage customer information and collection settings',
      },
      software: {
        title: 'Software',
        description: 'View and download the latest software version',
      },
      aiCreate: {
        title: 'Create AI image',
        description: 'Create AI themes and the backgrounds inside them',
      },
      deviceManagement: {
        title: 'Device Management',
        description: 'Manage devices, QR codes, and machine control',
      },
      userStaff: {
        title: 'Staff Management',
        description: 'Create and manage staff accounts for this login',
      },
      userManagement: {
        title: 'User Management',
        description: 'Manage regular users — view, edit, and control access',
      },
      staffManagement: {
        title: 'Staff Management',
        description: 'Manage staff members — view, edit, and promote to admin',
      },
      customFrame: {
        title: 'Custom Frame',
        description: 'Create and manage custom photo frames with advanced positioning',
      },
      album: {
        title: 'Album',
        description: 'Manage public albums: enable/disable frames and create/edit album pages',
      },
      landingPage: {
        title: 'Landing page',
        description: 'Edit the marketing /public/landing page (backend HTML), preview by section',
      },
      desktopManagement: {
        title: 'Desktop Management',
        description: 'Manage desktop machines and random codes',
      },
      updateSoftware: {
        title: 'Update Software',
        description: 'Upload and manage software update files',
      },
      emailManagement: {
        title: 'Email Management',
        description: 'Compose and send emails to users',
      },
      backgroundAi: {
        title: 'Background AI',
        description: 'Create AI background presets and enable them for user accounts',
      },
    },
  },
  vi: {
    welcome: 'Xin chào, {username}!',
    features: {
      mPhotoPro: {
        title: 'M-Photo Pro',
        description: 'Tải lên và quản lý ảnh của bạn',
      },
      mPhotoLite: {
        title: 'M-Photo Lite',
        description: 'Gallery server, icon PNG và đồng bộ từ app Android',
      },
      mPhotoMono: {
        title: 'M-Photo Mono',
        description: 'Thêm ảnh phụ và frame mono cho tài khoản của bạn',
      },
      livePhotos: {
        title: 'Live Photos',
        description: 'Xem và quản lý live photos của bạn',
      },
      videos: {
        title: 'Videos',
        description: 'Tải lên và quản lý video của bạn',
      },
      sePayPayment: {
        title: 'Thanh toán SePay',
        description: 'Cấu hình cổng SePay và tạo QR VietQR',
      },
      eventManagement: {
        title: 'Quản lý sự kiện',
        description: 'Quản lý các sự kiện và khung thời gian',
      },
      profile: {
        title: 'Hồ sơ',
        description: 'Xem và chỉnh sửa hồ sơ của bạn',
      },
      themes: {
        title: 'Themes',
        description: 'Quản lý themes và tổ chức frames',
      },
      customerInformation: {
        title: 'Thông tin khách hàng',
        description: 'Quản lý thông tin khách hàng và cài đặt thu thập',
      },
      software: {
        title: 'Phần mềm',
        description: 'Xem và tải phiên bản phần mềm mới nhất',
      },
      aiCreate: {
        title: 'Tạo hình AI',
        description: 'Tạo chủ đề AI và các nền bên trong chủ đề',
      },
      deviceManagement: {
        title: 'Quản lý thiết bị',
        description: 'Quản lý thiết bị, mã QR và điều khiển máy',
      },
      userStaff: {
        title: 'Quản lý nhân viên',
        description: 'Tạo và quản lý tài khoản nhân viên của tài khoản này',
      },
      userManagement: {
        title: 'Quản lý người dùng',
        description: 'Quản lý người dùng — xem, sửa và kiểm soát quyền truy cập',
      },
      staffManagement: {
        title: 'Quản lý nhân viên',
        description: 'Quản lý nhân viên — xem, sửa và thăng cấp admin',
      },
      customFrame: {
        title: 'Custom Frame',
        description: 'Tạo và quản lý khung ảnh tùy chỉnh với định vị nâng cao',
      },
      album: {
        title: 'Album',
        description: 'Quản lý album công khai: bật/tắt frame và tạo/sửa trang album',
      },
      landingPage: {
        title: 'Landing page',
        description: 'Chỉnh sửa trang marketing /public/landing (HTML backend), xem trước theo từng mục',
      },
      desktopManagement: {
        title: 'Quản lý Desktop',
        description: 'Quản lý máy desktop và mã ngẫu nhiên',
      },
      updateSoftware: {
        title: 'Cập nhật phần mềm',
        description: 'Tải lên và quản lý file cập nhật phần mềm',
      },
      emailManagement: {
        title: 'Quản lý Email',
        description: 'Soạn và gửi email cho người dùng',
      },
      backgroundAi: {
        title: 'Background AI',
        description: 'Tạo mục nền AI và bật tính năng đổi nền cho tài khoản user',
      },
    },
  },
  ko: {
    welcome: '환영합니다, {username}!',
    features: {
      mPhotoPro: {
        title: 'M-Photo Pro',
        description: '사진을 업로드하고 관리하세요',
      },
      mPhotoLite: {
        title: 'M-Photo Lite',
        description: '갤러리 서버, PNG 아이콘 및 Android 앱 동기화',
      },
      mPhotoMono: {
        title: 'M-Photo Mono',
        description: '계정에 보조 사진과 모노 프레임을 추가하세요',
      },
      livePhotos: {
        title: '라이브 포토',
        description: '라이브 포토를 보고 관리하세요',
      },
      videos: {
        title: '동영상',
        description: '동영상을 업로드하고 관리하세요',
      },
      sePayPayment: {
        title: 'SePay 결제',
        description: 'SePay 게이트웨이 설정 및 VietQR 생성',
      },
      eventManagement: {
        title: '이벤트 관리',
        description: '이벤트와 시간대를 관리하세요',
      },
      profile: {
        title: '프로필',
        description: '프로필을 보고 수정하세요',
      },
      themes: {
        title: '테마',
        description: '테마를 관리하고 프레임을 구성하세요',
      },
      customerInformation: {
        title: '고객 정보',
        description: '고객 정보 및 수집 설정을 관리하세요',
      },
      software: {
        title: '소프트웨어',
        description: '최신 소프트웨어 버전을 확인하고 다운로드하세요',
      },
      aiCreate: {
        title: 'AI 이미지 만들기',
        description: 'AI 주제와 그 안의 배경을 만듭니다',
      },
      deviceManagement: {
        title: '기기 관리',
        description: '기기, QR 코드 및 기기 제어를 관리하세요',
      },
      userStaff: {
        title: '직원 관리',
        description: '이 계정의 직원 계정을 만들고 관리하세요',
      },
      userManagement: {
        title: '사용자 관리',
        description: '일반 사용자 관리 — 조회, 수정 및 접근 제어',
      },
      staffManagement: {
        title: '스태프 관리',
        description: '스태프 관리 — 조회, 수정 및 관리자 승격',
      },
      customFrame: {
        title: '커스텀 프레임',
        description: '고급 위치 지정으로 커스텀 사진 프레임을 만들고 관리하세요',
      },
      album: {
        title: '앨범',
        description: '공개 앨범 관리: 프레임 켜기/끄기 및 앨범 페이지 생성/수정',
      },
      landingPage: {
        title: '랜딩 페이지',
        description: '마케팅 /public/landing 페이지(백엔드 HTML) 편집, 섹션별 미리보기',
      },
      desktopManagement: {
        title: '데스크톱 관리',
        description: '데스크톱 기기와 랜덤 코드를 관리하세요',
      },
      updateSoftware: {
        title: '소프트웨어 업데이트',
        description: '소프트웨어 업데이트 파일을 업로드하고 관리하세요',
      },
      emailManagement: {
        title: '이메일 관리',
        description: '사용자에게 이메일을 작성하고 보내세요',
      },
      backgroundAi: {
        title: 'Background AI',
        description: 'AI 배경 항목을 만들고 사용자 계정에 기능을 켜세요',
      },
    },
  },
  zh: {
    welcome: '欢迎，{username}！',
    features: {
      mPhotoPro: {
        title: 'M-Photo Pro',
        description: '上传并管理你的照片',
      },
      mPhotoLite: {
        title: 'M-Photo Lite',
        description: '图库服务器、PNG 图标，以及与 Android 应用同步',
      },
      mPhotoMono: {
        title: 'M-Photo Mono',
        description: '为账户添加副照片和单色相框',
      },
      livePhotos: {
        title: '实况照片',
        description: '查看并管理实况照片',
      },
      videos: {
        title: '视频',
        description: '上传并管理视频',
      },
      sePayPayment: {
        title: 'SePay 支付',
        description: '配置 SePay 网关并生成 VietQR',
      },
      eventManagement: {
        title: '活动管理',
        description: '管理活动和时间段',
      },
      profile: {
        title: '个人资料',
        description: '查看并编辑个人资料',
      },
      themes: {
        title: '主题',
        description: '管理主题并整理相框',
      },
      customerInformation: {
        title: '客户信息',
        description: '管理客户信息和收集设置',
      },
      software: {
        title: '软件',
        description: '查看并下载最新软件版本',
      },
      aiCreate: {
        title: '创建 AI 图片',
        description: '创建 AI 主题以及主题中的背景',
      },
      deviceManagement: {
        title: '设备管理',
        description: '管理设备、二维码和机器控制',
      },
      userStaff: {
        title: '员工管理',
        description: '为此账户创建并管理员工账号',
      },
      userManagement: {
        title: '用户管理',
        description: '管理普通用户 — 查看、编辑并控制访问权限',
      },
      staffManagement: {
        title: '员工管理',
        description: '管理员工 — 查看、编辑并提升为管理员',
      },
      customFrame: {
        title: '自定义相框',
        description: '创建并管理带高级定位的自定义照片相框',
      },
      album: {
        title: '相册',
        description: '管理公开相册：开关相框，创建或编辑相册页面',
      },
      landingPage: {
        title: '落地页',
        description: '编辑营销落地页 /public/landing（后端 HTML），按区块预览',
      },
      desktopManagement: {
        title: '桌面端管理',
        description: '管理桌面机器和随机码',
      },
      updateSoftware: {
        title: '软件更新',
        description: '上传并管理软件更新文件',
      },
      emailManagement: {
        title: '邮件管理',
        description: '撰写并发送邮件给用户',
      },
      backgroundAi: {
        title: 'Background AI',
        description: '创建 AI 背景预设，并为用户账户开启该功能',
      },
    },
  },
};
